import type { Content, Element, Root, Text } from "hast";
import type { Plugin } from "unified";
import { SKIP, visit } from "unist-util-visit";

/**
 * Known source attribution label types produced by the AI summarization pipeline.
 * Each key is the exact text that appears inside brackets in the markdown output.
 */
const SOURCE_LABEL_TYPES = new Set([
  "Documented",
  "Patient-reported",
  "Clinical assessment",
]);

/**
 * Regex that matches any known source attribution label in square brackets.
 * Captures the label text (without brackets) in group 1.
 * Uses a non-greedy match inside brackets to avoid spanning across multiple labels.
 */
const SOURCE_LABEL_RE =
  /\[(Documented|Patient-reported|Clinical assessment)\]/g;

const SKIP_TAGS = new Set(["code", "pre", "abbr", "a"]);

/**
 * Rehype plugin that detects source attribution labels like `[Documented]`,
 * `[Patient-reported]`, and `[Clinical assessment]` in text nodes and transforms
 * them into `<abbr>` elements with a `data-label-type` attribute.
 *
 * The `<abbr>` elements are then rendered as interactive tooltips via
 * react-markdown's `components` prop override in UnifiedMarkdown.
 */
export const rehypeSourceLabels: Plugin<[], Root> = function () {
  return function (tree: Root) {
    visit(tree, (node, index, parent) => {
      if (!parent || typeof index !== "number") return;

      if ((node as Element).type === "element") {
        const el = node as Element;
        if (el.tagName && SKIP_TAGS.has(el.tagName)) {
          return SKIP;
        }
      }

      if ((node as Text).type !== "text") return;

      const textNode = node as Text;
      const text = textNode.value;

      SOURCE_LABEL_RE.lastIndex = 0;
      const firstMatch = SOURCE_LABEL_RE.exec(text);
      if (!firstMatch) return;

      // Split text around label matches and build replacement nodes
      const nodes: Content[] = [];
      let lastIndex = 0;

      SOURCE_LABEL_RE.lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = SOURCE_LABEL_RE.exec(text)) !== null) {
        const labelText = match[1];
        const matchStart = match.index;
        const matchEnd = matchStart + match[0].length;

        if (!SOURCE_LABEL_TYPES.has(labelText)) continue;

        // Add text before this match
        if (matchStart > lastIndex) {
          nodes.push({
            type: "text",
            value: text.slice(lastIndex, matchStart),
          });
        }

        // Add the <abbr> element
        const labelType = labelText.toLowerCase().replace(/\s+/g, "-");
        nodes.push({
          type: "element",
          tagName: "abbr",
          properties: {
            className: ["source-label"],
            "data-label-type": labelType,
          },
          children: [{ type: "text", value: labelText }],
        });

        lastIndex = matchEnd;
      }

      if (nodes.length === 0) return;

      // Add remaining text after last match
      if (lastIndex < text.length) {
        nodes.push({ type: "text", value: text.slice(lastIndex) });
      }

      parent.children.splice(index, 1, ...nodes);
    });
  };
};
